const chapter = {
  id: 'kinematics',
  slug: 'kinematics',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Kinematics',
        hindi: 'गतिकी',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Easy–Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'basics',
      title: {
        english: 'Distance, Displacement, Speed & Velocity',
        hindi: 'दूरी, विस्थापन, चाल और वेग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Distance** (scalar) — total path length, always $\\ge 0$.',
        hindi: '**दूरी** (अदिश राशि) - कुल पथ लंबाई, हमेशा $\\ge 0$।',
            },
            {
              english: '**Displacement** (vector) — change in position vector, $\\Delta \\vec r = \\vec r_f - \\vec r_i$. Magnitude $\\le$ distance.',
        hindi: '**विस्थापन** (सदिश) - स्थिति सदिश में परिवर्तन, $\\Delta \\vec r = \\vec r_f - \\vec r_i$। परिमाण $\\le$ दूरी।',
            },
            {
              english: '**Speed** = distance / time (scalar). **Velocity** = displacement / time (vector).',
        hindi: '**गति** = दूरी/समय (अदिश राशि)। **वेग** = विस्थापन/समय (सदिश)।',
            },
            {
              english: '**Average velocity** $= \\dfrac{\\Delta \\vec r}{\\Delta t}$. **Instantaneous velocity** $\\vec v = \\dfrac{d\\vec r}{dt}$.',
        hindi: '**औसत वेग** $= \\dfrac{\\Delta \\vec r}{\\Delta t}$. **तात्कालिक वेग** $\\vec v = \\dfrac{d\\vec r}{dt}$.',
            },
            {
              english: '**Acceleration**: $\\vec a = \\dfrac{d\\vec v}{dt} = \\dfrac{d^2 \\vec r}{dt^2}$.',
        hindi: '**त्वरण**: $\\vec a = \\dfrac{d\\vec v}{dt} = \\dfrac{d^2 \\vec r}{dt^2}$.',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**Average speed** ≠ magnitude of average velocity (unless motion is straight-line without reversal).',
        hindi: '**औसत गति** ≠ औसत वेग का परिमाण (जब तक कि गति बिना उलटे सीधी रेखा में न हो)।',
          },
        },
      ],
    },
    {
      id: 'kinematic-eqns',
      title: {
        english: 'Equations of Uniformly Accelerated Motion (1D)',
        hindi: 'समान त्वरण गति के समीकरण (1D)',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Kinematic equations',
        hindi: 'गतिज समीकरण',
          },
          statement: {
            english: `For constant acceleration $a$, initial velocity $u$, after time $t$ velocity $v$, displacement $s$:
$$v = u + at$$
$$s = ut + \\tfrac{1}{2} a t^2$$
$$v^2 = u^2 + 2 a s$$
$$s_{\\text{n}} = u + \\tfrac{a}{2}(2n - 1)\\quad \\text{(distance in $n$-th second)}$$`,
            hindi: 'निरंतर त्वरण के लिए $a$, प्रारंभिक वेग $u$, समय के बाद $t$ वेग $v$, विस्थापन $s$: $$v = u + at$$ $$s = ut + \\tfrac{1}{2} a t^2$$ $$v^2 = u^2 + 2 a s$$ $$s_{\\text{n}} = u + \\tfrac{a}{2}(2n - 1)\\quad \\text{(distance in $n$-th second)}$$',
          },
        },
        {
          type: 'figure',
          svg: `
  <svg viewBox="0 0 360 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Velocity-time graph">
    <line x1="50" y1="170" x2="340" y2="170" stroke="#333" stroke-width="1.5" />
    <line x1="50" y1="20" x2="50" y2="170" stroke="#333" stroke-width="1.5" />
    <text x="330" y="188" font-size="12" font-family="serif">t</text>
    <text x="28" y="22" font-size="12" font-family="serif">v</text>
    <line x1="50" y1="140" x2="330" y2="40" stroke="#ff8a3d" stroke-width="2" />
    <line x1="50" y1="140" x2="50" y2="170" stroke="#999" stroke-dasharray="3 3" />
    <line x1="330" y1="40" x2="330" y2="170" stroke="#999" stroke-dasharray="3 3" />
    <text x="20" y="146" font-size="11">u</text>
    <text x="332" y="42" font-size="11">v</text>
    <polygon points="51,140 329,40 329,170 51,170" fill="rgba(255,138,61,0.18)" />
    <text x="175" y="160" font-size="12" fill="#a55a14">Area = displacement</text>
  </svg>`,
          caption: {
            english: 'Velocity–time graph for uniform acceleration: slope $= a$, area under curve $=$ displacement.',
        hindi: 'एकसमान त्वरण के लिए वेग-समय ग्राफ़: ढलान $= a$, वक्र के अंतर्गत क्षेत्र $=$ विस्थापन।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Slope of $x$–$t$ graph: instantaneous velocity.',
        hindi: '$x$–$t$ ग्राफ का ढलान: तात्कालिक वेग।',
            },
            {
              english: 'Slope of $v$–$t$ graph: instantaneous acceleration.',
        hindi: '$v$–$t$ ग्राफ का ढलान: तात्कालिक त्वरण।',
            },
            {
              english: 'Area under $v$–$t$ graph: displacement.',
        hindi: '$v$–$t$ ग्राफ़ के अंतर्गत क्षेत्र: विस्थापन।',
            },
            {
              english: 'Area under $a$–$t$ graph: change in velocity.',
        hindi: '$a$–$t$ ग्राफ़ के अंतर्गत क्षेत्र: वेग में परिवर्तन।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A car starts from rest and accelerates uniformly at $2\\,\\text{m/s}^2$ for 10 s. Find distance covered and final velocity.',
        hindi: 'एक कार आराम से शुरू होती है और 10 सेकंड के लिए $2\\,\\text{m/s}^2$ पर समान रूप से तेज होती है। तय की गई दूरी और अंतिम वेग ज्ञात कीजिए।',
          },
          solution: {
            english: '$v = 0 + 2 \\cdot 10 = 20$ m/s. $s = 0 + \\tfrac12 \\cdot 2 \\cdot 100 = 100$ m.',
        hindi: '$v = 0 + 2 \\cdot 10 = 20$ मी/से. $s = 0 + \\tfrac12 \\cdot 2 \\cdot 100 = 100$ मी.',
          },
        },
      ],
    },
    {
      id: 'free-fall',
      title: {
        english: 'Free Fall & Vertical Motion',
        hindi: 'मुक्त पतन और ऊर्ध्वाधर गति',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Take downward as positive (or upward, consistently). For free fall, $a = g \\approx 9.8\\,\\text{m/s}^2$. For a body projected upward with initial speed $u$:',
        hindi: 'नीचे की ओर सकारात्मक (या ऊपर की ओर, लगातार) लें। मुक्त गिरावट के लिए, $a = g \\approx 9.8\\,\\text{m/s}^2$। आरंभिक गति $u$ के साथ ऊपर की ओर प्रक्षेपित किसी पिंड के लिए:',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Time to reach maximum height: $t_{\\text{up}} = u/g$.',
        hindi: 'अधिकतम ऊंचाई तक पहुंचने का समय: $t_{\\text{up}} = u/g$।',
            },
            {
              english: 'Maximum height: $H = u^2/(2g)$.',
        hindi: 'अधिकतम ऊंचाई: $H = u^2/(2g)$।',
            },
            {
              english: 'Total time of flight (return to start): $T = 2u/g$.',
        hindi: 'उड़ान का कुल समय (शुरू करने के लिए वापसी): $T = 2u/g$।',
            },
            {
              english: 'Speed of return at start point: $u$ (same magnitude, opposite direction).',
        hindi: 'प्रारंभ बिंदु पर वापसी की गति: $u$ (समान परिमाण, विपरीत दिशा)।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A ball is thrown straight up at $20$ m/s ($g = 10$ m/s²). When does it return, and what is the max height?',
        hindi: 'एक गेंद को $20$ m/s ($g = 10$ m/s²) पर सीधे ऊपर फेंका जाता है। यह कब लौटता है, और अधिकतम ऊंचाई क्या है?',
          },
          solution: {
            english: '$T = 2 \\cdot 20 / 10 = 4$ s. $H = 400 / 20 = 20$ m.',
        hindi: '$T = 2 \\cdot 20/10 = 4$ s. $H = 400/20 = 20$ मी.',
          },
        },
      ],
    },
    {
      id: 'relative',
      title: {
        english: 'Relative Velocity',
        hindi: 'सापेक्ष वेग',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Relative velocity',
        hindi: 'सापेक्ष वेग **नदी-नाव**: वास्तविक । चौड़ाई में न्यूनतम समय: सीधा लक्ष्य रखें, (।',
          },
          statement: {
            english: `Velocity of $A$ relative to $B$: $\\vec v_{AB} = \\vec v_A - \\vec v_B$.
For 1D collinear motion: $v_{AB} = v_A - v_B$ (use signed values).`,
            hindi: '$B$ के सापेक्ष $A$ का वेग: $\\vec v_{AB} = \\vec v_A - \\vec v_B$। 1D संरेख गति के लिए: $v_{AB} = v_A - v_B$ (हस्ताक्षरित मानों का उपयोग करें)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**River–boat**: actual velocity = $\\vec v_{\\text{boat,water}} + \\vec v_{\\text{water,ground}}$. Min time across width $d$: aim straight across, $t = d / v_{\\text{boat}}$ (drift = $v_{\\text{water}} \\cdot t$).',
        hindi: '**नदी-नाव**: वास्तविक वेग = $\\vec v_{\\text{boat,water}} + \\vec v_{\\text{water,ground}}$। चौड़ाई $d$ पार करने के न्यूनतम समय के लिए नाव को सीधा पार की दिशा में रखें, तब $t = d / v_{\\text{boat}}$ और बहाव (drift) $= v_{\\text{water}} \\cdot t$ होगा।',
            },
            {
              english: '**Min drift / shortest path**: aim upstream by angle $\\theta$ such that $v_{\\text{boat}} \\sin\\theta = v_{\\text{water}}$ (only if $v_{\\text{boat}} > v_{\\text{water}}$).',
        hindi: '**न्यूनतम बहाव/सबसे छोटा रास्ता**: $\\theta$ कोण से धारा के प्रतिकूल लक्ष्य रखें ताकि $v_{\\text{boat}} \\sin\\theta = v_{\\text{water}}$ (केवल यदि $v_{\\text{boat}} > v_{\\text{water}}$ हो)।',
            },
            {
              english: '**Rain–man**: velocity of rain w.r.t. man $= \\vec v_{\\text{rain}} - \\vec v_{\\text{man}}$. Tilt umbrella in this direction.',
        hindi: '**रेन-मैन**: बारिश का वेग w.r.t. आदमी $= \\vec v_{\\text{rain}} - \\vec v_{\\text{man}}$. इस दिशा में झुकाएं छाता',
            },
          ],
        },
      ],
    },
    {
      id: 'projectile',
      title: {
        english: 'Projectile Motion',
        hindi: 'प्रक्षेप्य गति',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: `A particle launched with speed $u$ at angle $\\theta$ above horizontal, neglecting air drag. Independent horizontal and vertical motions:
• Horizontal: $a_x = 0,\\ v_x = u\\cos\\theta,\\ x = u\\cos\\theta \\cdot t$.
• Vertical: $a_y = -g,\\ v_y = u\\sin\\theta - gt,\\ y = u\\sin\\theta\\cdot t - \\tfrac12 g t^2$.`,
            hindi: 'एक कण हवा के खिंचाव की उपेक्षा करते हुए, क्षैतिज के ऊपर $\\theta$ कोण पर $u$ गति से प्रक्षेपित किया गया। स्वतंत्र क्षैतिज और ऊर्ध्वाधर गतियाँ: • क्षैतिज: $a_x = 0,\\\\\\\\ v_x = u\\cos\\theta,\\\\\\\\ x = u\\cos\\theta \\cdot t$. • लंबवत: $a_y = -g,\\\\\\\\ v_y = u\\sin\\theta - gt,\\\\\\\\ y = u\\sin\\theta\\cdot t - \\tfrac12 g t^2$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Standard projectile formulas',
        hindi: 'मानक प्रक्षेप्य सूत्र',
          },
          statement: {
            english: `• Time of flight: $T = \\dfrac{2 u \\sin\\theta}{g}$
• Maximum height: $H = \\dfrac{u^2 \\sin^2\\theta}{2g}$
• Horizontal range: $R = \\dfrac{u^2 \\sin 2\\theta}{g}$
• $R$ is **maximum** at $\\theta = 45°$, equal to $u^2/g$.
• Equation of trajectory: $y = x \\tan\\theta - \\dfrac{g x^2}{2 u^2 \\cos^2\\theta}$ — a parabola.`,
            hindi: '• उड़ान का समय: $T = \\dfrac{2 u \\sin\\theta}{g}$ • अधिकतम ऊंचाई: $H = \\dfrac{u^2 \\sin^2\\theta}{2g}$ • क्षैतिज सीमा: $R = \\dfrac{u^2 \\sin 2\\theta}{g}$ • $R$ $\\theta = 45°$ पर **अधिकतम** है, जो $u^2/g$ के बराबर है। • प्रक्षेपवक्र का समीकरण: $y = x \\tan\\theta - \\dfrac{g x^2}{2 u^2 \\cos^2\\theta}$ - एक परवलय।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Two angles giving same range: $\\theta$ and $90° - \\theta$ (since $\\sin 2\\theta$ is symmetric about $45°$).',
        hindi: 'समान सीमा देने वाले दो कोण: $\\theta$ और $90° - \\theta$ (चूंकि $\\sin 2\\theta$ $45°$ के बारे में सममित है)।',
            },
            {
              english: 'At highest point, vertical velocity $= 0$, horizontal velocity $= u\\cos\\theta$.',
        hindi: 'उच्चतम बिंदु पर, ऊर्ध्वाधर वेग $= 0$, क्षैतिज वेग $= u\\cos\\theta$।',
            },
            {
              english: 'For projectile from height $h$ horizontally with speed $u$: $T = \\sqrt{2h/g}$, $R = u\\sqrt{2h/g}$.',
        hindi: 'ऊंचाई $h$ से क्षैतिज रूप से गति $u$ के साथ प्रक्षेप्य के लिए: $T = \\sqrt{2h/g}$, $R = u\\sqrt{2h/g}$।',
            },
            {
              english: 'For projectile on inclined plane (angle $\\alpha$, launched at angle $\\theta$ to incline): $R_{\\text{incline}} = \\dfrac{2u^2 \\sin\\theta \\cos(\\theta + \\alpha)}{g \\cos^2\\alpha}$ (uphill).',
        hindi: 'झुके हुए तल पर प्रक्षेप्य के लिए (कोण $\\alpha$, झुकाव के लिए कोण $\\theta$ पर प्रक्षेपित): $R_{\\text{incline}} = \\dfrac{2u^2 \\sin\\theta \\cos(\\theta + \\alpha)}{g \\cos^2\\alpha}$ (ऊपर की ओर)।',
            },
          ],
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'A ball is projected at $30°$ with speed $20$ m/s ($g = 10$ m/s²). Find $T, H, R$.',
        hindi: 'एक गेंद को $30°$ गति $20$ m/s ($g = 10$ m/s²) पर प्रक्षेपित किया जाता है। $T, H, R$ खोजें।',
          },
          solution: {
            english: '$T = \\dfrac{2 \\cdot 20 \\cdot 0.5}{10} = 2$ s. $H = \\dfrac{400 \\cdot 0.25}{20} = 5$ m. $R = \\dfrac{400 \\cdot \\sin 60°}{10} = 20\\sqrt 3$ m.',
        hindi: '$T = \\dfrac{2 \\cdot 20 \\cdot 0.5}{10} = 2$ s. $H = \\dfrac{400 \\cdot 0.25}{20} = 5$ मी. $R = \\dfrac{400 \\cdot \\sin 60°}{10} = 20\\sqrt 3$ मी.',
          },
        },
      ],
    },
    {
      id: 'circular',
      title: {
        english: 'Uniform Circular Motion (Intro)',
        hindi: 'एकसमान वृत्तीय गति (परिचय)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Angular velocity**: $\\omega = d\\theta/dt$ (rad/s); $v = r\\omega$.',
        hindi: '**कोणीय वेग**: $\\omega = d\\theta/dt$ (रेड/एस); $v = r\\omega$.',
            },
            {
              english: '**Centripetal acceleration**: $a_c = v^2/r = \\omega^2 r$ (directed toward centre).',
        hindi: '**अभिकेंद्री त्वरण**: $a_c = v^2/r = \\omega^2 r$ (केंद्र की ओर निर्देशित)।',
            },
            {
              english: '**Period**: $T = 2\\pi/\\omega = 2\\pi r / v$. **Frequency**: $f = 1/T$.',
        hindi: '**अवधि**: $T = 2\\pi/\\omega = 2\\pi r / v$. **आवृत्ति**: $f = 1/T$.',
            },
            {
              english: 'For non-uniform circular motion, total acceleration = $\\sqrt{a_t^2 + a_c^2}$ where $a_t = dv/dt$ is tangential acceleration.',
        hindi: 'असमरूप वृत्तीय गति के लिए कुल त्वरण $= \\sqrt{a_t^2 + a_c^2}$ होता है, जहाँ $a_t = dv/dt$ स्पर्शरेखी त्वरण है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'v = u + at',
      desc: {
        english: '',
        hindi: 'वी = यू + एट',
      },
    },
    {
      latex: 's = ut + \\tfrac12 a t^2',
      desc: {
        english: '',
        hindi: 's = ut + 12 at 2',
      },
    },
    {
      latex: 'v^2 = u^2 + 2as',
      desc: {
        english: '',
        hindi: 'v 2 = u 2 + 2as',
      },
    },
    {
      latex: 'T = 2u\\sin\\theta/g',
      desc: {
        english: 'Projectile time of flight',
        hindi: 'उड़ान का प्रक्षेप्य समय',
      },
    },
    {
      latex: 'R = u^2 \\sin 2\\theta / g',
      desc: {
        english: 'Projectile range',
        hindi: 'प्रक्षेप्य सीमा',
      },
    },
    {
      latex: 'H = u^2 \\sin^2\\theta/(2g)',
      desc: {
        english: 'Projectile max height',
        hindi: 'प्रक्षेप्य की अधिकतम ऊंचाई',
      },
    },
    {
      latex: 'a_c = v^2/r',
      desc: {
        english: 'Centripetal',
        hindi: 'सेंट्रिपेटल',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Projectile',
      problem: {
        english: 'A projectile has range $R$ and reaches maximum height $H$. Show that $\\tan\\theta = 4H/R$.',
        hindi: 'एक प्रक्षेप्य की सीमा $R$ होती है और अधिकतम ऊंचाई $H$ तक पहुंचती है। दिखाएँ कि $\\tan\\theta = 4H/R$।',
      },
      answer: {
        english: '$\\tan\\theta = 4H/R$',
        hindi: '$\\tan\\theta = 4H/R$',
      },
      solution: {
        english: '$H = u^2\\sin^2\\theta/(2g), R = u^2\\sin 2\\theta/g = 2u^2\\sin\\theta\\cos\\theta/g$. Divide: $H/R = \\tan\\theta/4$.',
        hindi: '$H = u^2\\sin^2\\theta/(2g), R = u^2\\sin 2\\theta/g = 2u^2\\sin\\theta\\cos\\theta/g$. विभाजित करें: $H/R = \\tan\\theta/4$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Free fall',
      problem: {
        english: 'A body dropped from a height $h$ takes time $t$ to reach the ground. The distance covered in the last second is:',
        hindi: '$h$ ऊंचाई से गिराए गए एक पिंड को जमीन तक पहुंचने में $t$ का समय लगता है। अंतिम सेकंड में तय की गई दूरी है:',
      },
      answer: {
        english: '$g(t - 1/2)$ ... actually $s_t = u + \\tfrac{g}{2}(2t - 1) = \\tfrac{g}{2}(2t - 1)$',
        hindi: '$g(t - 1/2)$... वास्तव में $s_t = u + \\tfrac{g}{2}(2t - 1) = \\tfrac{g}{2}(2t - 1)$',
      },
      solution: {
        english: 'Use $s_n = u + \\tfrac{a}{2}(2n - 1)$ with $u = 0, a = g, n = t$.',
        hindi: '$u = 0, a = g, n = t$ के साथ $s_n = u + \\tfrac{a}{2}(2n - 1)$ का उपयोग करें।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Relative',
      problem: {
        english: 'Two trains 100 m and 80 m long move on parallel tracks at 60 km/h and 40 km/h in opposite directions. Time to cross each other?',
        hindi: '100 मीटर और 80 मीटर लंबी दो ट्रेनें विपरीत दिशाओं में 60 km/h और 40 km/h पर समानांतर पटरियों पर चलती हैं। एक दूसरे को पार करने का समय?',
      },
      answer: {
        english: '$\\dfrac{180}{(60 + 40) \\times 1000/3600} = \\dfrac{180 \\cdot 3600}{100000} = 6.48$ s',
        hindi: '$\\dfrac{180}{(60 + 40) \\times 1000/3600} = \\dfrac{180 \\cdot 3600}{100000} = 6.48$ s',
      },
      solution: {
        english: 'Relative speed = sum (opposite directions). Distance = sum of lengths.',
        hindi: 'सापेक्ष गति = योग (दिशाओं के विपरीत)। दूरी = लंबाई का योग.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Kinematics',
      problem: {
        english: 'A particle moves with $v(t) = 3t^2 - 6t + 4$ m/s. Find acceleration at $t = 2$ s and displacement in first 3 s.',
        hindi: 'एक कण $v(t) = 3t^2 - 6t + 4$ m/s के साथ चलता है। $t = 2$ s पर त्वरण और पहले 3 s में विस्थापन ज्ञात कीजिए।',
      },
      answer: {
        english: '$a(2) = 6$ m/s², $s = \\int_0^3 v\\,dt = [t^3 - 3t^2 + 4t]_0^3 = 27 - 27 + 12 = 12$ m',
        hindi: '$a(2) = 6$ m/s², $s = \\int_0^3 v\\,dt = [t^3 - 3t^2 + 4t]_0^3 = 27 - 27 + 12 = 12$ मी',
      },
      solution: {
        english: '$a = dv/dt = 6t - 6$. Displacement = $\\int v\\,dt$.',
        hindi: '$ए = डीवी/डीटी = 6टी - 6$। विस्थापन = $\\int v\\,dt$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Projectile',
      problem: {
        english: 'For what angle is range = max height of projectile?',
        hindi: 'किस कोण के लिए प्रक्षेप्य की सीमा = अधिकतम ऊंचाई है?',
      },
      answer: {
        english: '$\\tan\\theta = 4 \\Rightarrow \\theta = \\tan^{-1} 4$',
        hindi: '$\\tan\\theta = 4 \\Rightarrow \\theta = \\tan^{-1} 4$',
      },
      solution: {
        english: 'Set $H = R$: $u^2\\sin^2\\theta/2g = u^2 \\sin 2\\theta / g \\Rightarrow \\sin\\theta = 4\\cos\\theta$.',
        hindi: 'सेट करें $H = R$: $u^2\\sin^2\\theta/2g = u^2 \\sin 2\\theta / g \\Rightarrow \\sin\\theta = 4\\cos\\theta$.',
      },
    },
  ],
  next: {
    slug: 'laws-of-motion',
    title: 'Laws of Motion',
  },
};

export default chapter;
